import { createReducer, on } from '@ngrx/store';
import { isNil } from 'lodash';
import * as PanelActions from './panel.actions';
import { initialPanelState, IPanelState } from './panel.state';
import { IView } from '../models/IView';
import { IViews } from '../models/IViews';

export const panelReducer = createReducer(
  initialPanelState,

  on(PanelActions.updateShownMerkmale, (state, { merkmale }) => ({
    ...state,
    dossierView: {
      ...state.dossierView,
      shownMerkmale: merkmale,
    },
  })),

  on(PanelActions.togglePanel, (state, { selector }) => {
    const currentState = { ...state };
    const visibility = getVisibility(currentState, selector);
    updatePanels(currentState, selector, !visibility);

    return {
      ...state,
      dossierView: currentState.dossierView,
      searchView: currentState.searchView,
      feedbackView: currentState.feedbackView,
      configurationsView: currentState.configurationsView,
      psatzView: currentState.psatzView,
    };
  }),

  on(PanelActions.showPanel, (state, { selector }) => {
    const currentState = { ...state };
    updatePanels(currentState, selector, true);
    return {
      ...state,
      dossierView: currentState.dossierView,
      searchView: currentState.searchView,
      feedbackView: currentState.feedbackView,
      configurationsView: currentState.configurationsView,
      psatzView: currentState.psatzView,
    };
  }),

  on(PanelActions.hidePanel, (state, { selector }) => {
    const currentState = { ...state };
    updatePanels(currentState, selector, false);
    return {
      ...state,
      dossierView: currentState.dossierView,
      searchView: currentState.searchView,
      feedbackView: currentState.feedbackView,
      configurationsView: currentState.configurationsView,
      psatzView: currentState.psatzView,
    };
  }),

  on(PanelActions.addView, (state, { view }) => {
    const namedViews = { ...state.namedViews };
    return {
      ...state,
      namedViews: addView(namedViews, view),
    };
  }),

  on(PanelActions.applyDefaultView, (state) => {
    const namedViews = { ...state.namedViews };
    return {
      ...state,
      dossierView: applyDefaultView(namedViews),
    };
  }),

  on(PanelActions.changeDefaultView, (state, { viewName }) => {
    const namedViews = { ...state.namedViews };
    changeDefaultView(namedViews, viewName);
    return {
      ...state,
      namedViews: namedViews,
    };
  }),

  on(PanelActions.deleteNamedView, (state, { viewName }) => {
    const namedViews = { ...state.namedViews };
    deleteNamedView(namedViews, viewName);
    return {
      ...state,
      namedViews: namedViews,
    };
  }),

  on(PanelActions.applyNamedView, (state, { viewName }) => {
    const namedViews = { ...state.namedViews };
    const currentView = applyNamedView(namedViews, viewName);
    return {
      ...state,
      dossierView: currentView,
    };
  }),

  on(PanelActions.setNamedViews, (state, { views }) => ({
    ...state,
    namedViews: views,
  })),

  on(PanelActions.clearCurrentView, (state, { showAllMerkmale, openAllPanels }) => ({
    ...state,
    dossierView: getEmptyView(showAllMerkmale, openAllPanels),
  })),

  on(PanelActions.closeAllPanels, (state) => ({
    ...state,
    dossierView: getEmptyView(true, false),
  }))
);

// Helper functions moved from the ducks file
function updatePanels(state: IPanelState, selector: string, isVisible: boolean): void {
  if (selector.indexOf('search_') === 0) {
    const searchView = { ...state.searchView };
    const searchPanels = { ...searchView.panels };
    searchPanels[selector] = isVisible;
    searchView.panels = searchPanels;
    state.searchView = searchView;
  } else if (selector.indexOf('feedback_') === 0) {
    const feedbackView = { ...state.feedbackView };
    const feedbackPanels = { ...feedbackView.panels };
    feedbackPanels[selector] = isVisible;
    feedbackView.panels = feedbackPanels;
    state.feedbackView = feedbackView;
  } else if (selector.indexOf('configurations_') === 0) {
    const configurationsView = { ...state.configurationsView };
    const configurationsViewPanels = { ...configurationsView.panels };
    configurationsViewPanels[selector] = isVisible;
    configurationsView.panels = configurationsViewPanels;
    state.configurationsView = configurationsView;
  } else if (selector.indexOf('psatz_') === 0) {
    const psatzView = { ...state.psatzView };
    const psatzPanels = { ...psatzView.panels };
    psatzPanels[selector] = isVisible;
    psatzView.panels = psatzPanels;
    state.psatzView = psatzView;
  } else {
    const dossierView = { ...state.dossierView };
    const dossierPanels = { ...dossierView.panels };
    dossierPanels[selector] = isVisible;
    dossierView.panels = dossierPanels;
    state.dossierView = dossierView;
  }
}

function getVisibility(state: IPanelState, selector: string): boolean {
  const currentDossierView: IView = { ...state.dossierView };
  const currentSearchView: IView = { ...state.searchView };
  const currentFeedbackView: IView = { ...state.feedbackView };
  const currentConfigurationsView: IView = { ...state.configurationsView };
  const currentPsatzView: IView = { ...state.psatzView };

  let visibility = false;
  if (selector.indexOf('search_') === 0) {
    visibility = <boolean>currentSearchView.panels[selector];
  } else if (selector.indexOf('feedback_') === 0) {
    visibility = <boolean>currentFeedbackView.panels[selector];
  } else if (selector.indexOf('configurations_') === 0) {
    visibility = <boolean>currentConfigurationsView.panels[selector];
  } else if (selector.indexOf('psatz_') === 0) {
    visibility = <boolean>currentPsatzView.panels[selector];
  } else {
    const panelState = <boolean>currentDossierView.panels[selector];
    visibility = isNil(panelState) ? currentDossierView.openAllPanels : panelState;
  }
  return visibility;
}

function addView(views: IViews, view: IView): IViews {
  const currentView = <IView>{};
  currentView.viewName = view.viewName;
  currentView.id = null;
  currentView.isDefaultView = view.isDefaultView == null ? currentView.isDefaultView : view.isDefaultView;
  currentView.copyOf = view.id;
  currentView.panels = view.panels;
  currentView.openAllPanels = false;
  currentView.shownMerkmale = view.shownMerkmale;
  currentView.isSystem = false;
  currentView.showAllMerkmale = false;
  views[currentView.viewName] = { view: currentView };
  if (view.isDefaultView) {
    changeDefaultView(views, view.viewName);
  }

  return views;
}

function applyDefaultView(namedViews: IViews): IView {
  const currentView = <IView>{
    isDefaultView: false,
    viewName: null,
    panels: {},
    shownMerkmale: {},
  };

  for (const view in namedViews) {
    if (namedViews.hasOwnProperty(view) && namedViews[view].view.isDefaultView) {
      const config = namedViews[view].view;
      currentView.viewName = config.viewName;
      currentView.panels = config.panels;
      currentView.openAllPanels = config.openAllPanels;
      currentView.shownMerkmale = config.shownMerkmale;
      currentView.showAllMerkmale = config.showAllMerkmale;
      currentView.isSystem = config.isSystem;
    }
  }
  return currentView;
}

function applyNamedView(namedViews: IViews, viewName: string): IView {
  const currentView = <IView>{
    isDefaultView: true,
    viewName: viewName,
    id: null,
    panels: {},
    shownMerkmale: {},
  };
  for (const view in namedViews) {
    if (namedViews.hasOwnProperty(view)) {
      const config = namedViews[viewName].view;
      currentView.viewName = config.viewName;
      currentView.panels = config.panels;
      currentView.openAllPanels = config.openAllPanels;
      currentView.shownMerkmale = config.shownMerkmale;
      currentView.showAllMerkmale = config.showAllMerkmale;
      currentView.isSystem = config.isSystem;
    }
  }
  return currentView;
}

function changeDefaultView(namedViews: IViews, selector: string): void {
  for (const view in namedViews) {
    if (namedViews.hasOwnProperty(view)) {
      namedViews[view].view.isDefaultView = false;
      if (view === selector) {
        namedViews[view].view.isDefaultView = true;
      }
    }
  }
}

function deleteNamedView(namedViews: IViews, viewName: string): void {
  for (const view in namedViews) {
    if (namedViews.hasOwnProperty(view) && namedViews[view].view.viewName === viewName) {
      delete namedViews[view];
    }
  }
}

function getEmptyView(showAllMerkmale = false, openAllPanels = false): IView {
  const currentView = <IView>{
    isDefaultView: false,
    viewName: null,
    panels: {},
    openAllPanels,
    shownMerkmale: {},
    showAllMerkmale,
    isSystem: false,
  };
  return currentView;
}
